/**
 * The public API, typed.
 *
 * These types are hand-written, which is not where this should end up. #29 asks
 * for a generated client so drift is impossible, and #65 records why that is not
 * possible yet: `GET /lenses` reads `request.query_params` by hand, so FastAPI
 * publishes it as taking no parameters and returning an untyped object. A client
 * generated from that document would be unable to express a single real query.
 *
 * So: hand-written, deliberately, until #65 lands — at which point these types
 * should be deleted and generated. Until then treat this file as a claim about
 * the API that nothing enforces.
 *
 * The UI is a client with no privileged access (#29). Everything here is a
 * public endpoint; there is no admin path and no hidden parameter.
 */

// The API is served under the same origin in both development (Vite proxy) and
// production (one reverse proxy in front of both), so no base URL is baked into
// the bundle and one build artefact deploys anywhere (REQ-OPS-001).
import type { Freshness } from "./freshness";

const BASE = "/api/v1";

export type Datatype = "numeric" | "integer" | "boolean" | "enum" | "text" | "date";

export interface AttributeOption {
  value: string;
  label: string;
}

export interface Attribute {
  id: number;
  key: string;
  label: string;
  /** Short form for chips and narrow columns. Falls back to `label` (#67). */
  short_label: string | null;
  description: string | null;
  datatype: Datatype;
  unit_family: string;
  canonical_unit: string | null;
  /** The unit to *show*. Storage stays canonical; the registry says how to present it. */
  display_unit: string | null;
  /** Multiply a displayed number by this to get the canonical one. null means 1. */
  display_factor: number | null;
  group: string;
  applies_when: Record<string, unknown> | null;
  sort_order: number;
  is_core: boolean;
  is_expected: boolean;
  /** Shown in the same row as this attribute on the lens page (#214). */
  shown_with?: string | null;
  prominence: number;
  allow_multiple: boolean;
  is_filterable: boolean;
  is_sortable: boolean;
  /** REQ-DM-029: an enum whose options run least to most, so "this level or better" is meaningful. */
  options_ordered?: boolean;
  /** Granularity a slider should move in, in display units. */
  display_step: number | null;
  /**
   * How this attribute's values are distributed (REQ-DM-022).
   *
   * "geometric" means equal *ratios* are the meaningful step, so the control
   * scales logarithmically. Aperture is the clearest case: every stop is a
   * factor of root 2, and on a linear f-number track f/64 to f/128 — one stop
   * — took half the width while f/0.8 to f/1.6, also one stop, took 0.6%.
   *
   * Read from the registry, never from a list of keys here: an attribute added
   * by INSERT must not need a frontend change (REQ-DM-002).
   */
  scale: "linear" | "geometric";
  options: AttributeOption[];
}

export interface Registry {
  attributes: Attribute[];
  count: number;
  groups: { key: string; count: number }[];
}

/** A sensor coverage, with the rank that makes compatibility computable (#68). */
export interface Coverage {
  slug: string;
  name: string;
  /** Larger image circle, higher rank. A lens covers a body when lens >= body. */
  rank: number;
  is_body_format: boolean;
}

export interface Brand {
  slug: string;
  name: string;
  lenses: number;
}

/** REQ-WEB-035: brand and image-circle choices for one camera (#207). */
export interface FilterOptions {
  brands: Brand[];
  coverages: { slug: string; name: string; rank: number; lenses: number }[];
}

export interface Mount {
  slug: string;
  name: string;
  flange_distance_mm: number | null;
  /**
   * Which brands make lenses for this mount, observed from the lenses rather
   * than declared. A mount has no owning manufacturer (REQ-DM-020): brand and
   * mount are independent axes — Canon owns four mounts, Sony E hosts five
   * brands, and neither is a function of the other.
   */
  brands: Brand[];
  /**
   * Whether a lens covering LESS than the body's sensor can be mounted at all
   * (REQ-DM-018). Sony E: true, it crops. Canon EF: false, EF-S is keyed out.
   */
  accepts_smaller_coverage: boolean;
  /**
   * The sensor formats real *bodies* for this mount are made in (REQ-DM-021).
   *
   * Declared per mount, never inferred from `by_coverage`. Those are different
   * facts: four lenses in the database are full-frame glass in an X-mount
   * barrel, and no full-frame X-mount camera has ever been made. Inferring
   * would offer "Fujifilm X · Full frame" — a camera nobody owns.
   */
  body_formats: { slug: string; name: string; rank: number }[];
  /** Coverage of the *lenses* on this mount, which is a different question. */
  by_coverage: { coverage: string; lenses: number }[];
  lenses: number;
}

export interface MountList {
  mounts: Mount[];
  coverages: Coverage[];
  /** Every brand with at least one lens, for a brand filter (#72). */
  brands: Brand[];
  count: number;
}

/** Observed span of one numeric attribute, in canonical units (#66). */
export interface Range {
  key: string;
  datatype: Datatype;
  unit: string | null;
  min: number | null;
  max: number | null;
  /**
   * Where the bulk of the values lie (1st and 99th percentile), for seeding a
   * control's interactive extent (REQ-WEB-029).
   *
   * These narrow the *track*, never the *results*. Two pinhole lenses at f/96
   * and f/128 stretched the aperture slider eightfold and left 727 of 729
   * lenses inside 12% of it; a handle parked at either end sends no bound at
   * all, so those two stay in the list. A range that quietly dropped rows
   * would be the failure this project exists to avoid.
   */
  p_low: number | null;
  p_high: number | null;
  /** How many values built this range. A span from two points is barely a span. */
  count: number;
}

export interface Cell {
  value: number | string | boolean | null;
  source_id: number | null;
  /**
   * Accepted values for this cell when more than one exists. Note this counts
   * *sources*, not disagreements — two publishers reporting 373 g are two
   * competing values that agree.
   */
  /** REQ-API-019: the total accepted values, 1 when one source spoke. */
  competing_values?: number;
  /**
   * How many *different* values those sources gave (REQ-API-020). This is the
   * one that means a disagreement; `competing` alone was wrong about 1,653 of
   * 1,850 multi-source cells.
   */
  distinct_values?: number;
  /** Distinct publishers among them — independence (REQ-API-021). */
  distinct_publishers?: number;
  /** Do the sources agree once each one's printed precision is allowed for? (REQ-DM-024) */
  values_agree?: boolean;
  submitted_value?: number | string | boolean | null;
  submitted_unit_id?: number | null;
  /** On a price only (REQ-DM-031): the ISO 4217 currency `value` is written in. Never converted. */
  unit?: string | null;
  /**
   * Present and true when the column was computed rather than stored
   * (REQ-FX-010). A computed value carries no `source_id` on purpose: it is
   * not a claim anyone made, and attaching provenance would be a lie about
   * where the number came from.
   */
  computed?: boolean;
  /**
   * The spec cannot apply to this lens, so a null value is not a gap
   * (REQ-WEB-007). Either a rule says so (no `source_id`) or a source stated it
   * for this lens (REQ-DM-008, D45), in which case `source_id` is its citation.
   */
  state?: "not_applicable";
  /**
   * On a date cell (`released`): how precisely the source printed it. `value`
   * is that string ("2023", "2023-09", "2023-09-14"), never padded to a day
   * (REQ-DM-028).
   */
  date_precision?: "year" | "month" | "day";
}

export interface LensRow {
  slug: string;
  product_code: string;
  model_name: string;
  manufacturer: string;
  manufacturer_name?: string;
  mount: string;
  values: Record<string, Cell>;
}

export interface LensPage {
  items: LensRow[];
  columns: string[];
  sort: string;
  page_size: number;
  /** Matching lenses in total, not just this page — what a filter is judged by. */
  total: number;
  /** REQ-API-022: present when searching. Matches the camera or filters left out. */
  q_matches_elsewhere?: number;
  /** REQ-WEB-034: left out only because a filtered spec is unknown for them. */
  missing_data_excluded?: number;
  /**
   * REQ-WEB-042: present when sorting by a date. How many matched lenses have
   * no cited date; they are listed last in both directions.
   */
  undated?: number;
  has_more: boolean;
  next_cursor: string | null;
}

export interface Source {
  id: number;
  url: string;
  archive_url: string | null;
  title: string | null;
  publisher: string | null;
  source_type: string;
  retrieved_at: string | null;
}

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail: string;
  parameter?: string;
}

/** A 4xx the API described properly. Carries the offending parameter. */
export class ApiProblem extends Error {
  readonly problem: Problem;
  constructor(problem: Problem) {
    super(problem.detail || problem.title);
    this.name = "ApiProblem";
    this.problem = problem;
  }
}

/**
 * What to tell a visitor about a rejected request: the API's own words without
 * the developer-facing parts (REQ-FX-013 still holds: what was wrong, and where
 * in the formula). The API names a bad formula "computed column 'x': …" and
 * appends the parameter "computed[x]"; to a visitor that reads as code.
 */
export function problemText(p: Problem): string {
  const col = /^computed\[([^\]]*)\]$/.exec(p.parameter ?? "");
  if (!col) return withSpecName(p);
  const rest = p.detail.replace(/^computed column '[^']*':\s*/, "");
  return `The calculated column “${col[1]}” can’t be used: ${rest}`;
}

/**
 * Spec names by key, filled in when the list of specs loads, so an error can
 * name a spec the way the page does (#213).
 */
const specNames = new Map<string, string>();

/**
 * The API names the spec it is complaining about by its key: `'abc' is not a
 * number for 'weight_g'`. A visitor who followed a bad link has never seen
 * "weight_g"; they know "Weight". Only the parameter's own key is swapped, and
 * only when the spec is known, so any other text is left exactly as the API
 * wrote it.
 */
function withSpecName(p: Problem): string {
  // "weight_g" for a bare key, or "filter[weight_g][lte]" for a filter.
  const key = /^(?:filter\[([A-Za-z0-9_]+)\]\[[a-z]+\]|([A-Za-z0-9_]+))$/.exec(p.parameter ?? "");
  const name = specNames.get(key?.[1] ?? key?.[2] ?? "");
  if (!name) return p.detail;
  return p.detail.split(`'${key?.[1] ?? key?.[2]}'`).join(name);
}

/**
 * How long a request may take before the page says so (REQ-WEB-013: "never a
 * spinner that never ends"). The API answers in well under a second; a request
 * still open after this is not going to answer usefully.
 */
export const REQUEST_TIMEOUT_MS = 15_000;

/** Thrown when the server did not answer within REQUEST_TIMEOUT_MS. */
export class TimedOut extends Error {
  constructor() {
    super("The server is taking too long to answer.");
    this.name = "TimedOut";
  }
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  // One controller for both reasons to stop: the caller moved on (a newer
  // query), or the time ran out. Built by hand rather than AbortSignal.any,
  // which older iPhones do not have.
  const ac = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    ac.abort();
  }, REQUEST_TIMEOUT_MS);
  const forward = () => ac.abort();
  if (signal?.aborted) ac.abort();
  signal?.addEventListener("abort", forward);
  try {
    let res: Response;
    try {
      res = await fetch(BASE + path, {
        signal: ac.signal,
        headers: { accept: "application/json" },
      });
    } catch (cause) {
      if (timedOut) throw new TimedOut();
      // An aborted request is a normal part of typing into a filter, not a fault.
      if (signal?.aborted) throw cause;
      throw new Error("Could not reach the server. Check your connection and try again.");
    }

    if (!res.ok) {
      let problem: Problem | null = null;
      try {
        problem = (await res.json()) as Problem;
      } catch {
        problem = null;
      }
      if (problem && typeof problem.detail === "string") throw new ApiProblem(problem);
      // No "please report it": v1 has nowhere to report to (D22).
      // No status number either: "error 500" means nothing to a visitor (#213).
      throw new Error("Couldn't load this. Please try again.");
    }
    try {
      return (await res.json()) as T;
    } catch (cause) {
      // The body can stall too, after the headers arrived.
      if (timedOut) throw new TimedOut();
      throw cause;
    }
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", forward);
  }
}

export const api = {
  registry: (signal?: AbortSignal) =>
    get<Registry>("/attributes", signal).then((r) => {
      for (const a of r.attributes) specNames.set(a.key, a.label);
      return r;
    }),
  mounts: (signal?: AbortSignal) => get<MountList>("/mounts", signal),
  /** When the data last changed and what changed lately (REQ-WEB-044). */
  freshness: (signal?: AbortSignal) => get<Freshness>("/freshness", signal),
  /** The span of each date attribute in whole years (REQ-WEB-042). */
  yearRanges: (signal?: AbortSignal) =>
    get<{ ranges: Range[]; count: number }>("/stats/year-ranges", signal),
  filterOptions: (camera: Record<string, string>, signal?: AbortSignal) => {
    const qs = new URLSearchParams(camera).toString();
    return get<FilterOptions>(`/filter-options${qs ? `?${qs}` : ""}`, signal);
  },
  ranges: (signal?: AbortSignal) =>
    get<{ ranges: Range[]; count: number }>("/stats/ranges", signal),
  lenses: (query: string, signal?: AbortSignal) =>
    get<LensPage>(`/lenses${query ? `?${query}` : ""}`, signal),
  source: (id: number, signal?: AbortSignal) => get<Source>(`/sources/${id}`, signal),

  /**
   * The observed bounds of a computed column, over the whole matched set.
   *
   * `/stats/ranges` covers registry attributes; a computed column exists only
   * for the request that defines it, so it has no precomputed range. Rather
   * than add an endpoint, this asks the question the server can already answer:
   * sorting by the column is server-side over the full result set
   * (REQ-FX-010), so the first row ascending is the minimum and the first row
   * descending is the maximum. Two requests of one row each.
   *
   * Sampling the visible page instead would produce a slider whose ends are
   * whatever 25 lenses happened to load — bounds that move as you scroll are
   * worse than none.
   */
  computedRange: async (
    search: string,
    name: string,
    signal?: AbortSignal,
  ): Promise<Range | null> => {
    // The caller's search already carries a `sort`, and the API takes the
    // first occurrence of a repeated parameter — so appending another produced
    // two identical requests and a range whose min equalled its max. Replace
    // rather than append.
    const one = (dir: string) => {
      const p = new URLSearchParams(search);
      p.set("sort", `${dir}${name}`);
      p.set("page_size", "1");
      return get<LensPage>(`/lenses?${p.toString()}`, signal);
    };
    const [asc, desc] = await Promise.all([one(""), one("-")]);
    const lo = asc.items[0]?.values[name]?.value;
    const hi = desc.items[0]?.values[name]?.value;
    if (typeof lo !== "number" || typeof hi !== "number") return null;
    // p_low/p_high seed a slider's interactive extent (REQ-WEB-029). There are
    // no percentiles here — two rows cannot produce one — so the track is the
    // full observed span, which is honest rather than optimistic.
    return {
      key: name, datatype: "numeric", unit: null,
      min: lo, max: hi, p_low: lo, p_high: hi, count: asc.total,
    };
  },
};

/**
 * Canonical → display. The registry owns this conversion so no client has to
 * re-invent it, and so a lens that focuses to 250 mm is shown as focusing to
 * 0.25 m, which is how a lens is actually described.
 */
export function toDisplay(attr: Attribute, canonical: number): number {
  const f = attr.display_factor;
  return f && f !== 0 ? canonical / f : canonical;
}

/** Display → canonical, for building filter values the API will understand. */
export function toCanonical(attr: Attribute, shown: number): number {
  const f = attr.display_factor;
  return f && f !== 0 ? shown * f : shown;
}
